import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TYPES } from '../reducer';
import DB from '../../DAL/DB';
import { auth } from '../../Auth';
import _ from 'lodash'; // lodash is highly recommended for deep comparison

const meta = new DB.Meta();

export default function useGenerateQuery(props) {
  const { single, search, queryTypes, resultsElastic } = props;
  console.log('resultsElastic',resultsElastic?.length)
  const dispatch = useDispatch();

  const from = useSelector(state => state.from);
  const size = useSelector(state => state.size);
  // We don't select 'updateProjects' here to avoid circular dependency triggers
  // unless you explicitly need to re-trigger search when it changes.
  const updateProjects = useSelector(state => state.updateProjects);

  const [term, setTerm] = useState('');

  // ---------------------------------------------------------
  // 1. THE STABILIZERS (Ref Pattern)
  // ---------------------------------------------------------
  
  // We store the unstable 'queryTypes' and 'search' function in refs.
  // This allows us to access the LATEST version of them inside functions
  // WITHOUT adding them to the dependency arrays (which stops the loop).
  const queryTypesRef = useRef(queryTypes);
  const searchRef = useRef(search);

  // Always keep refs up to date with the latest props
  useEffect(() => {
    queryTypesRef.current = queryTypes;
    searchRef.current = search;
  }, [queryTypes, search]);


  // ---------------------------------------------------------
  // 2. QUERY GENERATOR
  // ---------------------------------------------------------
  const generateQuery = useCallback(() => {
    const currentFrom = from ?? 0;
    const currentSize = size ?? 1500;

    // Use the REF here. This is safe and stable.
    const ProjectQuery = queryTypesRef.current.project;

    const sourceFields = [
      "id", "site.siteOwnerName", "site.geocode_results.formatted_address",
      "site.geocode_results.geometry.location", "siteRepEmail", "siteRepName",
      "siteRepPhone", "status", "inspection", "intake.title", "siteNotes",
      "intakeCancellations", "updatedBy", "updatedByName", "created"
    ];

    const commonParams = {
        query: term,
        sort: [{ [`created._seconds`]: 'desc' }],
        filters: [{ terms: { [`status.sortable`]: ['To Be Scheduled', 'Scheduled'] } }],
        from: currentFrom,
        size: currentSize,
        sourceFields
    };

    if (auth.sunkaizenUser.type === 'field-user') {
      return new ProjectQuery({
        ...commonParams,
        or: [{ match: { [`user_index.${auth.sunkaizenUser.id}`]: 'inspector' } }],
      });
    } else {
      return new ProjectQuery({
        ...commonParams,
        or: [
          { match: { 'organization.id': auth.sunkaizenUser.organization.id } },
          { match: { [`user_index.${auth.sunkaizenUser.organization.id}`]: 'inspector' } },
          { match: { [`user_index.${auth.sunkaizenUser.organization.id}`]: 'client' } },
          { match: { [`user_index.${auth.sunkaizenUser.organization.id}`]: 'installer' } },
          { match: { [`user_index.${auth.sunkaizenUser.id}`]: 'inspector' } }
        ],
      });
    }
    // NOTICE: queryTypes is NOT in this array. Logic only re-runs if term/from/size change.
  }, [term, from, size]); 


  // ---------------------------------------------------------
  // 3. TRIGGER SEARCH
  // ---------------------------------------------------------
  useEffect(() => {
      if (single) return;

      const q = generateQuery();
      
      // Use the Ref to call search. 
      // This prevents the effect from re-running just because 'search' prop changed reference.
      if (searchRef.current) {
        searchRef.current(q);
      }
    },
    // Only re-run if these specific things change
    [generateQuery, single, updateProjects] 
  );


  // ---------------------------------------------------------
  // 4. HANDLE RESULTS (Stopping the Dispatch Loop)
  // ---------------------------------------------------------
  
  // Store the previous results to compare against incoming results
  const prevResultsRef = useRef([]);

  useEffect(() => {
      if (single) return;

      // CRITICAL CHECK:
      // The HOC returns a new array reference [] every time.
      // We must check if the CONTENT is actually different.
      // If you don't have lodash, use JSON.stringify(resultsElastic) === JSON.stringify(prevResultsRef.current)
      if (_.isEqual(resultsElastic, prevResultsRef.current)) {
          return; // Stop here if data is the same
      }

      const projectsMap = {};
      for (let p of resultsElastic || []) {
        if (p) projectsMap[p.id] = p;
      }

      console.log('Elastic Results Projects Map:', projectsMap);

      // Update the ref so we know we've processed this batch
      prevResultsRef.current = resultsElastic;

      dispatch({ type: TYPES.PROJECTS, payload: projectsMap });
    },
    [resultsElastic, single, dispatch]
  );

  // ---------------------------------------------------------
  // 5. SUBSCRIPTIONS
  // ---------------------------------------------------------
  useEffect(() => {
    return meta.subscribe('elasticUpdates', null, function ({ ok, data: [elasticUpdates] }) {
      if (ok) {
        const q = generateQuery();
        // Use ref here too
        if (searchRef.current) searchRef.current(q);
      }
    });
  }, [generateQuery]);

  return setTerm;
}